import {
  type FinishReconciliationInput,
  type ReconciliationDetail,
  reconciliationKeys,
} from '@taler/api-client'
import type { LocalDate, Money } from '@taler/domain'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref } from 'vue'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { useApi } from '@/app/api'
import { isIdempotencyPending, problemDetail, problemProperty } from '../internal/problem.ts'

export interface UseReconciliationOptions {
  readonly ledgerId: () => string | undefined
  readonly id: () => string | undefined
  readonly enabled: () => boolean
}

export type FinishOutcome =
  { readonly ok: true } | { readonly ok: false; readonly changedAccountIds: readonly string[] }

export interface UseReconciliation {
  readonly detail: ComputedRef<ReconciliationDetail | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly retrying: ComputedRef<boolean>
  readonly savingAccountIds: ComputedRef<ReadonlySet<string>>
  readonly hasSaved: ComputedRef<boolean>
  readonly changedAccountIds: ComputedRef<ReadonlySet<string>>
  readonly lineErrorDetails: ComputedRef<ReadonlyMap<string, string>>
  readonly isFinishing: ComputedRef<boolean>
  readonly finishErrorDetail: ComputedRef<string | undefined>
  readonly finishErrorPending: ComputedRef<boolean>
  readonly isAbandoning: ComputedRef<boolean>
  saveLine(accountId: string, observedBalance: Money): Promise<void>
  clearLine(accountId: string): Promise<void>
  updateDate(observedDate: LocalDate): Promise<void>
  finish(applyReallocations: boolean): Promise<FinishOutcome>
  abandon(): Promise<void>
  retry(): void
}

export function useReconciliation(options: UseReconciliationOptions): UseReconciliation {
  const api = useApi()
  const queryClient = useQueryClient()

  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.id() !== undefined,
  )
  const queryKey = computed(() => reconciliationKeys.detail(options.ledgerId() ?? '', options.id() ?? ''))

  const detailQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => api.reconciliations.get(options.ledgerId()!, options.id()!, signal),
    enabled: active,
    retry: false,
  })

  const savingAccountIds = ref(new Set<string>())
  const hasSaved = ref(false)
  const changedAccountIds = ref(new Set<string>())
  const lineErrors = ref(new Map<string, Error>())
  const isFinishing = ref(false)
  const finishError = ref<Error | null>(null)
  const isAbandoning = ref(false)

  function setDetail(next: ReconciliationDetail): void {
    queryClient.setQueryData(queryKey.value, next)
  }

  function invalidateLedgerWide(): Promise<void> {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return Promise.resolve()
    return Promise.all(
      reconciliationKeys
        .invalidatedByWrite(ledgerId)
        .map((key) => queryClient.invalidateQueries({ queryKey: key })),
    ).then(() => undefined)
  }

  async function saveLine(accountId: string, observedBalance: Money): Promise<void> {
    const ledgerId = options.ledgerId()
    const id = options.id()
    if (ledgerId === undefined || id === undefined) return
    savingAccountIds.value = new Set(savingAccountIds.value).add(accountId)
    try {
      const next = await api.reconciliations.setLine(ledgerId, id, accountId, observedBalance)
      setDetail(next)
      hasSaved.value = true
      changedAccountIds.value = new Set(
        [...changedAccountIds.value].filter((candidate) => candidate !== accountId),
      )
      if (lineErrors.value.has(accountId)) {
        const remaining = new Map(lineErrors.value)
        remaining.delete(accountId)
        lineErrors.value = remaining
      }
    } catch (thrown) {
      lineErrors.value = new Map(lineErrors.value).set(accountId, thrown as Error)
    } finally {
      const remaining = new Set(savingAccountIds.value)
      remaining.delete(accountId)
      savingAccountIds.value = remaining
    }
  }

  async function clearLine(accountId: string): Promise<void> {
    const ledgerId = options.ledgerId()
    const id = options.id()
    if (ledgerId === undefined || id === undefined) return
    savingAccountIds.value = new Set(savingAccountIds.value).add(accountId)
    try {
      await api.reconciliations.clearLine(ledgerId, id, accountId)
      hasSaved.value = true
      await detailQuery.refetch()
    } finally {
      const remaining = new Set(savingAccountIds.value)
      remaining.delete(accountId)
      savingAccountIds.value = remaining
    }
  }

  async function updateDate(observedDate: LocalDate): Promise<void> {
    const ledgerId = options.ledgerId()
    const id = options.id()
    if (ledgerId === undefined || id === undefined) return
    setDetail(await api.reconciliations.update(ledgerId, id, observedDate))
  }

  async function finish(applyReallocations: boolean): Promise<FinishOutcome> {
    const ledgerId = options.ledgerId()
    const id = options.id()
    const current = detailQuery.data.value
    if (ledgerId === undefined || id === undefined || current === undefined) return { ok: true }
    isFinishing.value = true
    finishError.value = null
    const input: FinishReconciliationInput = {
      applyReallocations,
      expected: current.rows
        .filter((row) => row.observedBalance !== undefined && row.reconciledSnapshotId === undefined)
        .map((row) => ({ accountId: row.accountId, computedBalance: row.computedBalance })),
    }
    try {
      const finished = await api.reconciliations.finish(ledgerId, id, input)
      setDetail(finished)
      await invalidateLedgerWide()
      return { ok: true }
    } catch (thrown) {
      finishError.value = thrown as Error
      const changed = problemProperty<readonly string[]>(thrown as Error, 'accountIds') ?? []
      if (changed.length > 0) {
        changedAccountIds.value = new Set(changed)
        await detailQuery.refetch()
      }
      return { ok: false, changedAccountIds: changed }
    } finally {
      isFinishing.value = false
    }
  }

  async function abandon(): Promise<void> {
    const ledgerId = options.ledgerId()
    const id = options.id()
    if (ledgerId === undefined || id === undefined) return
    isAbandoning.value = true
    try {
      await api.reconciliations.abandon(ledgerId, id)
      await invalidateLedgerWide()
    } finally {
      isAbandoning.value = false
    }
  }

  return {
    detail: computed(() => detailQuery.data.value),
    isLoading: computed(() => active.value && detailQuery.isPending.value),
    error: computed(() => detailQuery.error.value),
    notFound: computed(() => isNotFound(detailQuery.error.value)),
    retrying: retrying(detailQuery),
    savingAccountIds: computed(() => savingAccountIds.value),
    hasSaved: computed(() => hasSaved.value),
    changedAccountIds: computed(() => changedAccountIds.value),
    lineErrorDetails: computed(
      () =>
        new Map(
          [...lineErrors.value].map(([accountId, error]) => [
            accountId,
            problemDetail(error) ?? error.message,
          ]),
        ),
    ),
    isFinishing: computed(() => isFinishing.value),
    finishErrorDetail: computed(() => problemDetail(finishError.value)),
    finishErrorPending: computed(() => isIdempotencyPending(finishError.value)),
    isAbandoning: computed(() => isAbandoning.value),
    saveLine,
    clearLine,
    updateDate,
    finish,
    abandon,
    retry: () => {
      void detailQuery.refetch()
    },
  }
}
